import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DateTimePicker from "../utils/DateTimePicker";
import {
  getOtherMedicalAvailability,
  saveOtherMedicalAvailability,
} from "../../redux/apis/Availability/alternativeMedicalAvailabilitySlice";
import UserInfo from "./AllAvailabilities";

// ================= CONFIG =================
const days = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const defaultHalves = [
  { name: "First Half", startTime: "09:00", endTime: "13:00", isOpen: false },
  { name: "Second Half", startTime: "14:00", endTime: "18:00", isOpen: false },
];

// ================= DATE HELPERS =================
const formatDate = (date) => date.toLocaleDateString("en-CA");

// ✅ ONLY FUTURE 7 DAYS
const getNext7Days = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);

    return {
      day: days[d.getDay()],
      date: formatDate(d),
    };
  });
};

const AlternativeMedicalAvailability = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { single, loading, error } = useSelector(
    (state) => state.alternativeAvailability,
  );

  const token =
    useSelector((state) => state.auth.token) || localStorage.getItem("token");

  const user =
    useSelector((state) => state.auth.user) ||
    JSON.parse(localStorage.getItem("user"));

  // ================= STATE =================
  const [weekly, setWeekly] = useState(
    getNext7Days().map((d) => ({
      day: d.day,
      date: d.date,
      isHoliday: false,
      halves: defaultHalves.map((h) => ({ ...h })),
    })),
  );

  const [manualMode, setManualMode] = useState(false);

  // ================= FETCH =================
  useEffect(() => {
    if (token) {
      dispatch(getOtherMedicalAvailability({ token }));
    }
  }, [token, dispatch]);

  // ================= 🔥 BACKEND → UI MAPPING =================
  useEffect(() => {
    if (!single?.weeklyAvailability) return;

    const base = getNext7Days().map((d) => ({
      day: d.day,
      date: d.date,
      isHoliday: false,
      halves: defaultHalves.map((h) => ({ ...h, isOpen: false })),
    }));

    single.weeklyAvailability.forEach((dayData) => {
      const index = base.findIndex((d) => d.date === dayData.date);

      if (index !== -1) {
        base[index].isHoliday = dayData.isHoliday;

        (dayData.sessions || []).forEach((session) => {
          const halfIndex = base[index].halves.findIndex(
            (h) => h.name === session.name,
          );

          if (halfIndex !== -1) {
            base[index].halves[halfIndex] = {
              name: session.name,
              startTime: session.startTime,
              endTime: session.endTime,
              isOpen: session.status === "open",
            };
          }
        });
      }
    });

    setWeekly(base);
  }, [single]);

  // ================= AUTH CHECK =================
  if (!token || !user) {
    return (
      <div className="p-6 text-center text-red-600">❌ Please login first</div>
    );
  }

  const isToday = (date) =>
    new Date(date).toDateString() === new Date().toDateString();

  // ================= ACTIONS =================
  const toggleHoliday = (i) => {
    const copy = [...weekly];
    copy[i].isHoliday = !copy[i].isHoliday;

    if (copy[i].isHoliday) {
      copy[i].halves = copy[i].halves.map((h) => ({
        ...h,
        isOpen: false,
      }));
    }

    setWeekly(copy);
  };

  const toggleHalf = (i, hIndex) => {
    const copy = [...weekly];
    copy[i].halves[hIndex].isOpen = !copy[i].halves[hIndex].isOpen;
    setWeekly(copy);
  };

  const updateHalfTime = (i, hIndex, field, value) => {
    const copy = [...weekly];
    const half = copy[i].halves[hIndex];

    const updated = { ...half, [field]: value };

    if (updated.startTime >= updated.endTime) {
      alert("Start time must be before end time");
      return;
    }

    copy[i].halves[hIndex] = updated;
    setWeekly(copy);
  };

  // ================= SAVE =================
  const handleSave = async () => {
    const payload = {
      weeklyAvailability: weekly.map((d) => ({
        day: d.day,
        date: d.date,
        isHoliday: d.isHoliday,
        status: d.isHoliday ? "closed" : "open",
        sessions: d.halves.map((h) => ({
          name: h.name,
          startTime: h.startTime,
          endTime: h.endTime,
          status: h.isOpen ? "open" : "closed",
        })),
      })),
      customDates: [],
    };

    const res = await dispatch(
      saveOtherMedicalAvailability({ data: payload, token }),
    );

    if (saveOtherMedicalAvailability.fulfilled.match(res)) {
      navigate("/dashboard/medical-service");
    }
  };

  // ================= UI =================
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h2 className="text-3xl font-bold text-blue-600 mb-6">
        📅 Alternative Medical Availability
      </h2>

      <UserInfo />

      {error && <p className="text-red-600">{error}</p>}

      <button
        onClick={() => setManualMode(!manualMode)}
        className="mb-4 text-blue-600 underline"
      >
        {manualMode ? "Switch to Auto Mode" : "Switch to Manual Mode"}
      </button>

      {weekly.map((day, i) => (
        <div key={day.date} className="p-4 mb-4 border rounded shadow">
          <div className="flex justify-between">
            <div>
              <h4 className="font-bold">{day.day}</h4>

              <p className={isToday(day.date) ? "text-blue-600 font-bold" : ""}>
                {day.date} {isToday(day.date) && "(Today)"}
              </p>

              {day.isHoliday && (
                <p className="text-red-600 font-bold">Holiday</p>
              )}
            </div>

            <button
              onClick={() => toggleHoliday(i)}
              className="bg-gray-500 text-white px-2 py-1 rounded"
            >
              {day.isHoliday ? "Remove Holiday" : "Mark Holiday"}
            </button>
          </div>

          {!day.isHoliday &&
            day.halves.map((half, hIndex) => (
              <DateTimePicker
                key={hIndex}
                half={half}
                onToggle={() => toggleHalf(i, hIndex)}
                onTimeChange={(f, v) => updateHalfTime(i, hIndex, f, v)}
              />
            ))}
        </div>
      ))}

      <button
        onClick={handleSave}
        className="w-full bg-green-600 text-white py-3 rounded"
        disabled={loading}
      >
        {loading ? "Saving..." : "💾 Save Availability"}
      </button>
    </div>
  );
};

export default AlternativeMedicalAvailability;
